<template>
  <b-card no-body class="mb-1 activity-filter">
    <b-card-body class="pb-1">
      <!--
        Row one: the three filters that carry most of the work.

        Search, module and action are first because they answer the two questions people
        actually arrive with — "what happened to this record/person" and "show me every
        deletion" — and putting them on one line keeps both one click away.
      -->
      <b-row>
        <b-col cols="12" md="4" class="mb-1">
          <label class="filter-label">Search</label>
          <b-input-group class="input-group-merge">
            <b-form-input
              :value="value.search"
              placeholder="Person, record, or description"
              debounce="400"
              @update="(val) => emit('search', val)"
            />
            <b-input-group-append is-text>
              <feather-icon icon="SearchIcon" class="text-muted" />
            </b-input-group-append>
          </b-input-group>
        </b-col>

        <b-col v-if="!isLocked('module')" cols="12" md="4" class="mb-1">
          <label class="filter-label">
            Module
            <span v-if="selectedModules.length" class="text-primary">({{ selectedModules.length }})</span>
          </label>
          <v-select
            :value="selectedModules"
            :options="moduleOptions"
            :reduce="(option) => option.value"
            label="label"
            multiple
            :close-on-select="false"
            placeholder="All modules"
            @input="(val) => emit('module', val)"
          >
            <!--
              Counts come from the facet endpoint and describe the current date window, so an
              empty module is visibly empty rather than an option that silently returns nothing.
            -->
            <template #option="{ label, count }">
              <span>{{ label }}</span>
              <span v-if="count !== undefined" class="text-muted small ml-50">{{ count }}</span>
            </template>
          </v-select>
        </b-col>

        <b-col v-if="!isLocked('action')" cols="12" md="4" class="mb-1">
          <label class="filter-label">
            Action
            <span v-if="selectedActions.length" class="text-primary">({{ selectedActions.length }})</span>
          </label>
          <v-select
            :value="selectedActions"
            :options="actionOptions"
            :reduce="(option) => option.value"
            label="label"
            multiple
            :close-on-select="false"
            placeholder="All actions"
            @input="(val) => emit('action', val)"
          >
            <template #option="{ label, count }">
              <span>{{ label }}</span>
              <span v-if="count !== undefined" class="text-muted small ml-50">{{ count }}</span>
            </template>
          </v-select>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="3" class="mb-1">
          <label class="filter-label">Performed by</label>
          <!--
            Pick a known actor, or type any name.

            The dropdown can only offer people who appear in the current window, so on a quiet
            window it is empty and a plain select would be a dead control. Typed text falls back to
            `actor_name`, which matches the name snapshot stored on each row — so it also finds
            people whose account has since been deleted, and the addresses behind failed sign-ins,
            neither of which has a user id to select.
          -->
          <v-select
            :value="actorSelection"
            :options="actorOptions"
            :reduce="(option) => option.value"
            label="label"
            taggable
            :push-tags="false"
            :clearable="true"
            :create-option="(name) => ({ label: name, value: `name:${name}` })"
            placeholder="Anyone — or type a name"
            @input="onActorSelected"
          >
            <template #option="{ label, count }">
              <span>{{ label }}</span>
              <span v-if="count !== undefined" class="text-muted small ml-50">{{ count }}</span>
            </template>
            <template #no-options>
              <div class="text-muted small py-50 px-1">
                No recent activity by that name — press enter to search for it anyway
              </div>
            </template>
          </v-select>
        </b-col>

        <b-col v-if="!isLocked('category')" cols="12" md="3" class="mb-1">
          <label class="filter-label">Category</label>
          <v-select
            :value="value.category"
            :options="options.categories || []"
            :reduce="(option) => option.value"
            label="label"
            :clearable="true"
            placeholder="All categories"
            @input="(val) => emit('category', val)"
          />
        </b-col>

        <b-col v-if="!isLocked('outcome')" cols="12" md="3" class="mb-1">
          <label class="filter-label">Outcome</label>
          <v-select
            :value="value.outcome"
            :options="options.outcomes || []"
            :reduce="(option) => option.value"
            label="label"
            :clearable="true"
            placeholder="Any outcome"
            @input="(val) => emit('outcome', val)"
          />
        </b-col>

        <b-col cols="12" md="3" class="mb-1">
          <!--
            A threshold, not a picker. "At least a warning" is what someone wants when scanning
            for problems; picking warning-but-not-error is a filter nobody asks for.
          -->
          <label class="filter-label">Minimum severity</label>
          <v-select
            :value="value.min_severity"
            :options="options.severities || []"
            :reduce="(option) => option.value"
            label="label"
            :clearable="true"
            placeholder="Any severity"
            @input="(val) => emit('min_severity', val)"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="3" class="mb-1">
          <label class="filter-label">From</label>
          <b-form-datepicker
            :value="value.from"
            reset-button
            :max="value.to || today"
            :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            placeholder="Any time"
            @input="(val) => emit('from', val)"
          />
        </b-col>

        <b-col cols="12" md="3" class="mb-1">
          <label class="filter-label">To</label>
          <b-form-datepicker
            :value="value.to"
            reset-button
            :min="value.from"
            :max="today"
            :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
            placeholder="Now"
            @input="(val) => emit('to', val)"
          />
        </b-col>

        <b-col cols="12" md="6" class="mb-1">
          <label class="filter-label">Quick range</label>
          <div>
            <b-button
              v-for="period in periods"
              :key="period.value"
              size="sm"
              class="mr-50 mb-50"
              :variant="value.period === period.value ? 'primary' : 'outline-secondary'"
              @click="emit('period', value.period === period.value ? '' : period.value)"
            >
              {{ period.label }}
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!--
        Advanced filters are collapsed by default.

        These are the ones used during an investigation rather than on an ordinary visit — trace
        everything from one IP, or follow a single request id across the rows it produced. Showing
        them always would make the common case look complicated.
      -->
      <b-collapse v-model="showAdvanced">
        <hr class="my-1">
        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label class="filter-label">IP address</label>
            <b-form-input
              :value="value.ip_address"
              placeholder="e.g. 102.176.94.12"
              debounce="500"
              @update="(val) => emit('ip_address', val)"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <label class="filter-label">Changed field</label>
            <b-form-input
              :value="value.changed_field"
              placeholder="e.g. status"
              debounce="500"
              @update="(val) => emit('changed_field', val)"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <label class="filter-label">Request / correlation ID</label>
            <b-form-input
              :value="value.correlation_id"
              placeholder="Paste an ID"
              debounce="500"
              @update="(val) => emit('correlation_id', val)"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <label class="filter-label">Performed by</label>
            <v-select
              :value="value.actor_type"
              :options="options.actor_types || []"
              :reduce="(option) => option.value"
              label="label"
              :clearable="true"
              placeholder="Any actor type"
              @input="(val) => emit('actor_type', val)"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col cols="12" md="3" class="mb-1">
            <label class="filter-label">HTTP method</label>
            <v-select
              :value="value.method"
              :options="methods"
              :clearable="true"
              placeholder="Any method"
              @input="(val) => emit('method', val)"
            />
          </b-col>

          <b-col cols="12" md="3" class="mb-1">
            <label class="filter-label">Status code</label>
            <b-form-input
              :value="value.status_code"
              placeholder="e.g. 403"
              debounce="500"
              @update="(val) => emit('status_code', val)"
            />
          </b-col>

          <b-col cols="12" md="6" class="mb-1 d-flex align-items-end">
            <b-form-checkbox
              v-if="!isLocked('has_changes')"
              :checked="value.has_changes === true || value.has_changes === 'true'"
              switch
              @input="(val) => emit('has_changes', val ? 'true' : '')"
            >
              Only entries that changed data
            </b-form-checkbox>
          </b-col>
        </b-row>
      </b-collapse>

      <div class="d-flex justify-content-between align-items-center flex-wrap mt-50">
        <div>
          <b-button size="sm" variant="flat-primary" @click="showAdvanced = !showAdvanced">
            <feather-icon :icon="showAdvanced ? 'ChevronUpIcon' : 'SlidersIcon'" class="mr-50" />
            {{ showAdvanced ? 'Hide advanced' : 'Advanced filters' }}
          </b-button>

          <b-button
            v-if="activeFilterCount > 0"
            size="sm"
            variant="flat-danger"
            @click="$emit('reset')"
          >
            <feather-icon icon="XIcon" class="mr-50" />
            Clear {{ activeFilterCount }} filter{{ activeFilterCount === 1 ? '' : 's' }}
          </b-button>
        </div>

        <!--
          Saved views. Each one is a question someone actually arrives with, so it is one click
          instead of four dropdowns — and they double as a hint that the filters compose.
        -->
        <div>
          <b-button
            v-for="view in views"
            :key="view.label"
            size="sm"
            variant="outline-primary"
            class="ml-50 mb-50"
            @click="$emit('apply-view', view.filters)"
          >
            <feather-icon :icon="view.icon" class="mr-50" />
            {{ view.label }}
          </b-button>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BCollapse,
  BFormInput, BFormDatepicker, BInputGroup, BInputGroupAppend, BFormCheckbox,
} from 'bootstrap-vue'

import vSelect from 'vue-select'
import { get } from 'lodash'

/**
 * The activity log filter bar.
 *
 * Controlled: it holds no filter state of its own beyond whether the advanced panel is open, and
 * emits `update:<field>` for the page to apply. That keeps a single source of truth, which matters
 * because the page also mirrors the filters into the URL — two copies would let the two drift and
 * leave shared links showing a different view than the person who sent them saw.
 *
 * The option lists come from the server (`/activity-logs/filter-options`), not from constants here.
 * The taxonomy lives in one place on the API, so a module or action added there reaches these
 * dropdowns on the next deploy.
 */
export default {
  name: 'ActivityLogFilter',
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BCollapse,
    BFormInput,
    BFormDatepicker,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    vSelect,
  },
  props: {
    /** The page's current filter state. */
    value: {
      type: Object,
      required: true,
    },
    /** The full taxonomy, from the server. */
    options: {
      type: Object,
      default: () => ({}),
    },
    /** Which values occur in the current window, with counts. */
    facets: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Filters the parent page fixes itself. Their controls are hidden rather than disabled: a
     * greyed-out dropdown invites someone to try to change a filter the screen will not honour.
     */
    lockedFields: {
      type: Array,
      default: () => [],
    },
    /** Replaces the default saved views, for a screen where the defaults do not apply. */
    savedViews: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      showAdvanced: false,
      today: new Date(),
      periods: [
        { value: 'today', label: 'Today' },
        { value: '24h', label: '24 hours' },
        { value: '7d', label: '7 days' },
        { value: '30d', label: '30 days' },
        { value: '90d', label: '90 days' },
      ],
      methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
      defaultSavedViews: [
        {
          label: 'Failures',
          icon: 'AlertTriangleIcon',
          filters: { outcome: 'failure', period: '7d' },
        },
        {
          label: 'Denied access',
          icon: 'LockIcon',
          filters: { outcome: 'denied', period: '7d' },
        },
        {
          label: 'Data changes',
          icon: 'EditIcon',
          filters: { has_changes: 'true', period: '7d' },
        },
        {
          label: 'Deletions',
          icon: 'Trash2Icon',
          filters: { action: 'delete,bulk_delete', period: '30d' },
        },
        {
          label: 'Security',
          icon: 'ShieldIcon',
          filters: { category: 'security', period: '30d' },
        },
      ],
    };
  },
  computed: {
    /** The parent's views when given, otherwise the defaults, minus any a locked field would fix. */
    views() {
      const available = this.savedViews || this.defaultSavedViews;
      return available.filter(
        (view) => !Object.keys(view.filters).some((field) => this.isLocked(field))
      );
    },

    /**
     * Multi-value filters travel as comma-separated strings so they survive a URL round trip,
     * but v-select needs arrays. These two computeds are the only place that conversion happens.
     */
    selectedModules() {
      return this.toArray(this.value.module);
    },
    selectedActions() {
      return this.toArray(this.value.action);
    },

    /**
     * Facet counts merged onto the taxonomy.
     *
     * Values with rows in the window sort first and carry their count; the rest stay available
     * but unannotated, so a module with no recent activity is still selectable without looking
     * like the better choice.
     */
    moduleOptions() {
      return this.withCounts(this.options.modules, this.facets.modules);
    },
    actionOptions() {
      return this.withCounts(this.options.actions, this.facets.actions);
    },
    actorOptions() {
      return get(this.facets, 'actors', []);
    },

    /** Whichever of the two actor filters is active, in the form v-select expects. */
    actorSelection() {
      if (this.value.user_id) return this.value.user_id;
      if (this.value.actor_name) return `name:${this.value.actor_name}`;
      return null;
    },

    /** Drives the "Clear N filters" button, so there is never a hidden narrowing. */
    activeFilterCount() {
      const ignored = ['page', 'limit', 'sort_by'];
      return Object.keys(this.value).filter((key) => {
        if (ignored.includes(key)) return false;
        // A locked field is the screen's definition, not a filter the reader applied, so counting
        // it would offer to "clear" something clearing cannot remove.
        if (this.isLocked(key)) return false;
        const val = this.value[key];
        return val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0);
      }).length;
    },
  },
  methods: {
    isLocked(field) {
      return this.lockedFields.includes(field);
    },

    /**
     * Routes the choice to the right filter.
     *
     * A selected option carries a user id; a typed one is prefixed `name:` by `create-option`. The
     * two are mutually exclusive, so whichever is set clears the other — leaving both would narrow
     * to rows matching an id *and* a name, which is never what was meant.
     */
    onActorSelected(selected) {
      if (!selected) {
        this.$emit('update:filter', { field: 'user_id', value: '' });
        this.$emit('update:filter', { field: 'actor_name', value: '' });
        return;
      }

      const raw = typeof selected === 'object' ? selected.value : selected;

      if (typeof raw === 'string' && raw.startsWith('name:')) {
        this.$emit('update:filter', { field: 'user_id', value: '' });
        this.$emit('update:filter', { field: 'actor_name', value: raw.slice(5) });
        return;
      }

      this.$emit('update:filter', { field: 'actor_name', value: '' });
      this.$emit('update:filter', { field: 'user_id', value: raw });
    },

    /** Emits the array form as a comma-separated string, matching what the API accepts. */
    emit(field, val) {
      this.$emit('update:filter', { field, value: Array.isArray(val) ? val.join(',') : val });
    },
    toArray(val) {
      if (!val) return [];
      return Array.isArray(val) ? val : String(val).split(',').filter(Boolean);
    },
    withCounts(taxonomy = [], facets = []) {
      const counts = (facets || []).reduce((map, facet) => {
        map[facet.value] = facet.count;
        return map;
      }, {});

      return (taxonomy || [])
        .map((option) => ({ ...option, count: counts[option.value] }))
        .sort((a, b) => (b.count || 0) - (a.count || 0) || a.label.localeCompare(b.label));
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.activity-filter {
  .filter-label {
    font-size: 0.8rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
  }
}
</style>
